import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, PageContainer } from '@react-apps/design-system';
import { useCreateContentItem } from '../hooks/use-content-items';
import {
  useCreateExamQuestionRunner,
  useInvalidateExams,
  useValidateExamImport,
} from '../hooks/use-exams';
import { useInvalidateInstitutions } from '../hooks/use-institutions';
import { useRequestQueue } from '../hooks/use-request-queue';
import {
  parseExamCsv,
  readExamZip,
  toValidateImportRows,
  validateImages,
  type ImportError,
  type ImportRowDraft,
  type ParsedExamCsv,
} from '../exam-import';
import {
  clearImport,
  loadImportFiles,
  loadResumableImport,
  preparedFromOverride,
  saveImportFiles,
  saveImportState,
  type ImportQuestionState,
  type ImportState,
} from '../exam-import-session';
import {
  convertIssue,
  preflightImages,
  preflightChosenFile,
  preparedToBase64,
  type PreflightIssue,
  type PreparedImage,
} from '@react-apps/content-editor';
import { describeIssue, describeOrigin } from '../image-preflight-messages';
import {
  CONTENT_STATUS_LABELS,
  EXAM_TYPE_LABELS,
  Field,
  SectionCard,
  getApiErrorMessage,
  notifyFailed,
} from '../display';
import { RequestQueuePanel } from '../components/RequestQueuePanel';
import { ImportIssueCard } from '../components/ImportIssueCard';

/**
 * Três requisições simultâneas — o que foi pedido explicitamente. Vale como
 * teto: cada uma carrega as imagens daquela questão em base64.
 */
const IMPORT_CONCURRENCY = 3;

/**
 * Importação de uma prova inteira a partir de um `.zip` (CSV + `imagens/`).
 *
 * O fluxo é client-driven: o front extrai o arquivo, valida a estrutura
 * (passada 1), confere os **bytes** de cada imagem (pré-flight), pede ao backend
 * a validação de domínio (passada 2), e só depois de confirmado cria a prova e
 * enfileira **uma requisição por questão**. O backend nunca recebe o arquivo
 * bruto.
 *
 * A ordem não é acidental: tudo que pode recusar uma questão é decidido **antes
 * do primeiro POST**. Descobrir no meio da fila deixa a prova pela metade.
 */
export function ExamImportPage() {
  const navigate = useNavigate();

  const validate = useValidateExamImport();
  const createItem = useCreateContentItem();
  const createQuestion = useCreateExamQuestionRunner();
  const invalidateExams = useInvalidateExams();
  const invalidateInstitutions = useInvalidateInstitutions();
  const queue = useRequestQueue(IMPORT_CONCURRENCY);

  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [clientErrors, setClientErrors] = useState<ImportError[]>([]);
  const [parsed, setParsed] = useState<ParsedExamCsv | null>(null);
  const [createdExamId, setCreatedExamId] = useState<string | null>(null);
  const [batchStarted, setBatchStarted] = useState(false);

  // --- Lote que sobreviveu a um F5 / a sair da página ---
  const [resumable, setResumable] = useState<ImportState | null>(null);
  const [isResuming, setIsResuming] = useState(false);
  /** Questões que já tinham subido antes desta sessão da fila. */
  const [resumedDone, setResumedDone] = useState(0);
  /** O zip como veio, guardado para o reenvio não precisar pedir o arquivo. */
  const zipFileRef = useRef<Blob | null>(null);
  const examLabelRef = useRef('');
  const startedAtRef = useRef(0);
  /** O que não está na fila atual e não deve sumir do registro ao salvar. */
  const baselineRef = useRef<ImportQuestionState[]>([]);

  // --- Pré-flight de imagem ---
  const [imageIssues, setImageIssues] = useState<PreflightIssue[]>([]);
  const [imagesFixed, setImagesFixed] = useState<
    { filename: string; note: string }[]
  >([]);
  const [scanProgress, setScanProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [busyFilename, setBusyFilename] = useState<string | null>(null);

  /**
   * As imagens já classificadas pelo pré-flight. Em `ref`, não em estado, por
   * dois motivos: os bytes das aceitas continuam comprimidos no zip (nada a
   * renderizar), e os jobs da fila leem daqui **na hora de rodar** — é isso que
   * faz "corrigir a imagem e reenviar" funcionar sem recriar o job.
   */
  const preparedRef = useRef<Map<string, PreparedImage>>(new Map());

  /** Um input escondido serve todas as pendências — quem pediu fica no ref. */
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replacingRef = useRef<PreflightIssue | null>(null);

  // --- Dropzone ---
  const [isDragging, setIsDragging] = useState(false);
  const zipInputRef = useRef<HTMLInputElement>(null);
  /**
   * `dragenter`/`dragleave` disparam também nos filhos da área (o botão, o
   * texto): contando a profundidade, a moldura não pisca quando o cursor
   * atravessa um deles.
   */
  const dragDepthRef = useRef(0);
  /**
   * Número de sequência do carregamento em curso. Remover ou trocar o arquivo
   * no meio da leitura cria a corrida clássica — a leitura anterior termina
   * depois e reescreve o estado com dados de um arquivo que já saiu da tela.
   * O que voltar fora de sequência é descartado.
   */
  const loadSeqRef = useRef(0);

  const serverErrors = validate.data?.errors ?? [];
  const institution = validate.data?.institution ?? null;
  const institutionError = validate.data?.institutionError ?? null;
  const existingExam = validate.data?.existingExam ?? null;
  const canConfirm =
    !!parsed &&
    !!institution &&
    serverErrors.length === 0 &&
    // Pré-flight bloqueia: imagem em formato recusado só falharia mais tarde,
    // com parte da prova já importada.
    imageIssues.length === 0 &&
    !batchStarted;

  /**
   * Repete o dry-run com o mesmo lote, sem F5 e sem reabrir o zip: é o que
   * fecha o ciclo "o erro apontou para a tela de instituições → cadastrei →
   * volto e valido de novo". O `parsed` vive no estado desta página, e o zip no
   * `zipFileRef`, então nada precisa ser lido de novo.
   *
   * Invalida o cache de instituições antes: a que acabou de ser cadastrada não
   * está nele, e é o back que resolve a sigla — mas o rótulo da tela de
   * confirmação sai do cache.
   */
  const revalidate = () => {
    if (!parsed) return;
    invalidateInstitutions();
    validate.mutate({
      institutionLabel: parsed.institutionLabel,
      examYear: parsed.examYear,
      examVariant: parsed.examVariant,
      rows: toValidateImportRows(parsed.rows),
    });
  };

  const resetAll = () => {
    setReadError(null);
    setClientErrors([]);
    setParsed(null);
    setCreatedExamId(null);
    setBatchStarted(false);
    setImageIssues([]);
    setImagesFixed([]);
    setScanProgress(null);
    setBusyFilename(null);
    preparedRef.current = new Map();
    validate.reset();
    createItem.reset();
    queue.reset();
  };

  const handleFile = async (file: File) => {
    resetAll();
    const seq = ++loadSeqRef.current;
    const isStale = () => loadSeqRef.current !== seq;
    setFileName(file.name);
    setFileSize(file.size);
    zipFileRef.current = file;
    setIsReading(true);
    try {
      const zip = await readExamZip(file);
      if (isStale()) return;
      const { parsed: parsedCsv, errors } = parseExamCsv(zip.csvText);
      const imageErrors = parsedCsv
        ? validateImages(parsedCsv.rows, zip.images)
        : [];
      const allErrors = [...errors, ...imageErrors];
      if (!parsedCsv || allErrors.length > 0) {
        setClientErrors(allErrors);
        return;
      }

      // Passada 1b: o formato real de cada figura, pelos bytes. Descomprimir o
      // lote leva um instante visível numa prova com muitas figuras, daí o
      // progresso.
      const { prepared, issues } = await preflightImages(
        parsedCsv.rows,
        zip.images,
        (done, total) => {
          if (!isStale()) setScanProgress({ done, total });
        },
      );
      if (isStale()) return;
      preparedRef.current = prepared;
      setImageIssues(issues);
      setScanProgress(null);
      setParsed(parsedCsv);

      // O dry-run roda mesmo com pendência de imagem: ele é sobre texto e regra
      // de domínio, e o admin merece ver todos os problemas de uma vez.
      validate.mutate({
        institutionLabel: parsedCsv.institutionLabel,
        examYear: parsedCsv.examYear,
        examVariant: parsedCsv.examVariant,
        rows: toValidateImportRows(parsedCsv.rows),
      });
    } catch (err) {
      if (isStale()) return;
      setReadError(
        getApiErrorMessage(err, 'Não foi possível ler o arquivo enviado.'),
      );
    } finally {
      if (!isStale()) setIsReading(false);
    }
  };

  /** Tira o arquivo da tela e volta a zona ao estado vazio. */
  const removeFile = () => {
    loadSeqRef.current += 1;
    resetAll();
    setFileName(null);
    setFileSize(null);
  };

  const isBusy = isReading || batchStarted;
  const pendingOnResumable =
    resumable?.questions.filter((q) => q.status !== 'done') ?? [];

  /** O que está acontecendo com o arquivo, para a pastilha dizer. */
  const fileStatus = scanProgress
    ? `verificando imagens ${scanProgress.done}/${scanProgress.total}`
    : isReading
      ? 'lendo…'
      : validate.isPending
        ? 'validando no servidor…'
        : parsed
          ? `${parsed.rows.length} ${parsed.rows.length === 1 ? 'questão' : 'questões'}`
          : null;

  /** Só `.zip`: recusar aqui é mais claro do que falhar dentro do `readExamZip`. */
  const acceptZip = (files: FileList | File[]) => {
    const list = [...files];
    // Uma importação é uma prova só. Pegar o primeiro de cinco em silêncio é
    // como alguém importa a prova errada sem perceber.
    if (list.length > 1) {
      removeFile();
      setReadError(
        `Envie um arquivo por vez — você soltou ${list.length}. Uma importação é uma prova só.`,
      );
      return;
    }
    const file = list[0];
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      removeFile();
      setFileName(file.name);
      setFileSize(file.size);
      setReadError(
        `"${file.name}" não é um .zip. Envie o arquivo compactado com o .csv na raiz e as figuras em imagens/.`,
      );
      return;
    }
    void handleFile(file);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (isBusy) return;
    // Sem o preventDefault no dragover o browser recusa o drop (é assim que a
    // API funciona: cancelar o evento é o que declara a área como destino).
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (event: React.DragEvent) => {
    if (isBusy) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (isBusy) return;
    const files = event.dataTransfer.files;
    if (files?.length) acceptZip(files);
  };

  const resolveIssue = (filename: string, note: string) => {
    setImagesFixed((prev) => [...prev, { filename, note }]);
    setImageIssues((prev) =>
      prev.filter((issue) => issue.filename !== filename),
    );
  };

  const handleConvert = async (issue: PreflightIssue) => {
    setBusyFilename(issue.filename);
    try {
      const outcome = await convertIssue(issue);
      if ('failure' in outcome) {
        // Convertível em teoria, não aqui: ou este browser não decodifica o
        // formato (HEIC fora do Safari), ou converteu e ainda passa do teto. A
        // pendência continua, com o motivo certo, e para de prometer conversão.
        setImageIssues((prev) =>
          prev.map((current) =>
            current.filename === issue.filename
              ? {
                  ...current,
                  reason: outcome.failure.reason,
                  blob: undefined,
                  rejection: outcome.failure.rejection,
                }
              : current,
          ),
        );
        return;
      }
      preparedRef.current.set(issue.filename, outcome.prepared);
      resolveIssue(issue.filename, describeOrigin(outcome.prepared.origin));
    } finally {
      setBusyFilename(null);
    }
  };

  const handleReplaceChosen = async (file: File) => {
    const issue = replacingRef.current;
    replacingRef.current = null;
    if (!issue) return;

    setBusyFilename(issue.filename);
    try {
      // O nome NÃO muda: o token `[](imagens/<nome>)` do CSV aponta para ele, e
      // o back decide o mime pelos bytes, não pela extensão.
      const result = await preflightChosenFile(
        issue.filename,
        issue.positions,
        file,
      );
      if ('prepared' in result) {
        preparedRef.current.set(issue.filename, result.prepared);
        resolveIssue(
          issue.filename,
          result.prepared.source === 'memory'
            ? describeOrigin(result.prepared.origin)
            : 'arquivo trocado',
        );
        return;
      }
      setImageIssues((prev) =>
        prev.map((current) =>
          current.filename === issue.filename ? result.issue : current,
        ),
      );
    } finally {
      setBusyFilename(null);
    }
  };

  /** As imagens que não estão no zip — convertidas e trocadas à mão. */
  const memoryOverrides = () =>
    [...preparedRef.current.entries()].flatMap(([filename, prepared]) =>
      prepared.source === 'memory'
        ? [
            {
              filename,
              blob: prepared.blob,
              mime: prepared.mime,
              origin: prepared.origin,
            },
          ]
        : [],
    );

  /**
   * Enfileira uma questão por linha. Mesmo caminho para o import novo e para o
   * reenvio do que faltou — o `run` lê as imagens de `preparedRef` na hora de
   * rodar, então correções feitas depois já entram no payload.
   */
  const enqueueQuestions = (examId: string, rows: ImportRowDraft[]) => {
    /** Os bytes de um conjunto de nomes, na hora em que a fila chega na linha. */
    const bytesFor = (filenames: string[]) =>
      Promise.all(
        filenames.map(async (filename) => {
          const prepared = preparedRef.current.get(filename);
          if (!prepared) {
            throw new Error(`imagem "${filename}" não está mais no lote`);
          }
          return { filename, base64: await preparedToBase64(prepared) };
        }),
      );

    queue.enqueue(
      rows.map((row) => ({
        id: `question-${row.position}`,
        label: `Questão ${row.position}`,
        run: async () => {
          const images = await bytesFor(row.imageFilenames);
          // Lista à parte: no back o dono do arquivo é o comentário, não a
          // questão, e o teto de 10 imagens é por body.
          const commentImages = await bytesFor(row.commentImageFilenames);
          await createQuestion(examId, {
            position: row.position,
            format: row.format,
            stem: row.stem,
            alternatives:
              row.format === 'MULTIPLE_CHOICE' ? row.alternatives : undefined,
            correctAlternativeId: row.correctAlternativeId,
            expectedAnswer: row.expectedAnswer,
            images: images.length > 0 ? images : undefined,
            classification: row.classification || undefined,
            comment: row.comment.trim() || undefined,
            commentAuthor: row.commentAuthor || undefined,
            commentImages: commentImages.length > 0 ? commentImages : undefined,
          });
        },
      })),
    );
  };

  /**
   * Retoma o lote interrompido: reabre o zip guardado, remonta as imagens
   * (as do zip mais as convertidas/trocadas) e devolve à fila **só** o que não
   * está `done`. Reenviar é seguro porque o backend é idempotente por
   * `(prova, position)` — questão que subiu e não respondeu volta como ela
   * mesma, não como uma segunda.
   */
  const handleResume = async (state: ImportState) => {
    setIsResuming(true);
    try {
      const files = await loadImportFiles(state.examId);
      if (!files) {
        setResumable(null);
        setReadError(
          'O arquivo desta importação não está mais guardado neste navegador. Envie o .zip de novo.',
        );
        return;
      }

      const zip = await readExamZip(files.zip);
      const { parsed: parsedCsv, errors } = parseExamCsv(zip.csvText);
      if (!parsedCsv) {
        setResumable(null);
        setClientErrors(errors);
        return;
      }

      const pending = new Set(
        state.questions
          .filter((q) => q.status !== 'done')
          .map((q) => q.position),
      );
      const rows = parsedCsv.rows.filter((row) => pending.has(row.position));

      // Só as figuras que as questões pendentes citam: descomprimir o zip
      // inteiro de novo para reenviar 12 de 80 questões seria trabalho jogado
      // fora, e o progresso passaria a contar imagens que ninguém vai usar.
      const cited = new Set(rows.flatMap((row) => row.imageFilenames));
      const { prepared, issues } = await preflightImages(
        rows,
        new Map([...zip.images].filter(([name]) => cited.has(name))),
        (done, total) => setScanProgress({ done, total }),
      );
      for (const override of files.overrides) {
        prepared.set(override.filename, preparedFromOverride(override));
      }
      // Pendência que sobrou é imagem cuja correção não foi guardada: aparece
      // como card, a questão falha na fila, e o "tentar de novo" já sai com o
      // arquivo corrigido.
      const unresolved = issues.filter(
        (issue) => !prepared.has(issue.filename),
      );

      preparedRef.current = prepared;
      zipFileRef.current = files.zip;
      examLabelRef.current = state.examLabel;
      startedAtRef.current = state.startedAt;
      baselineRef.current = state.questions.filter((q) => q.status === 'done');

      setScanProgress(null);
      setParsed(parsedCsv);
      setImageIssues(unresolved);
      setFileName(state.examLabel);
      setFileSize(files.zip.size);
      setResumedDone(baselineRef.current.length);
      setCreatedExamId(state.examId);
      setResumable(null);
      setBatchStarted(true);
      enqueueQuestions(state.examId, rows);
    } finally {
      setIsResuming(false);
    }
  };

  const handleDiscardResumable = async (examId: string) => {
    setResumable(null);
    await clearImport(examId);
  };

  const handleConfirm = async () => {
    if (!parsed || !institution) return;
    let exam;
    try {
      exam = await createItem.mutateAsync({
        type: 'EXAM',
        body: {
          year: parsed.examYear,
          institutionId: institution.id,
          variant: parsed.examVariant,
          // Importar sempre cria DONO: o back só aceita questão nova em prova
          // real (`INVALID_CONTENT_LINK_EXAM_KIND`). Simulado aponta para as
          // questões de uma prova depois — ver primum-monolith
          // `docs/reference/modules/content.md` § Dono vs. apontador.
          examKind: 'REAL_EXAM',
          examType: parsed.examType,
        },
      });
    } catch (err) {
      // A mensagem já está inline por `createItem.error`, mas o botão fica no
      // fim de uma tela longa (arquivo + erros + pendências de imagem + tipo da
      // prova): quem clicou pode não ver a linha que apareceu logo acima dele.
      // O toast diz o que falhou e, principalmente, que **nada** foi criado —
      // sem ele o clique parece ter sumido no vazio.
      notifyFailed(err, 'A prova não foi criada — nenhuma questão foi enviada');
      return;
    }
    setCreatedExamId(exam.id);
    setBatchStarted(true);
    setResumedDone(0);
    baselineRef.current = [];
    examLabelRef.current = `(${institution.initials}-${institution.uf}) ${institution.name} · ${parsed.examVariant} ${parsed.examYear}`;
    startedAtRef.current = Date.now();

    // O peso (o zip e as imagens corrigidas) é gravado UMA vez, aqui: é o que
    // permite reenviar depois de um F5 sem pedir o arquivo de novo.
    if (zipFileRef.current) {
      void saveImportFiles({
        examId: exam.id,
        zip: zipFileRef.current,
        overrides: memoryOverrides(),
      });
    }
    enqueueQuestions(exam.id, parsed.rows);
  };

  useEffect(() => {
    void loadResumableImport().then(setResumable);
  }, []);

  // Soltar o arquivo FORA da dropzone faria o browser navegar para ele, levando
  // a página — e uma importação em andamento — embora. Enquanto esta tela está
  // montada, só a dropzone aceita drop; o resto da janela ignora.
  useEffect(() => {
    const swallow = (event: DragEvent) => event.preventDefault();
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  // `items.length` é a guarda contra o instante entre "lote começou" e a fila
  // aparecer no estado: sem ela, uma fila vazia leria como "acabou sem erro".
  // Quem falhou já está parado esperando o clique (a fila não reenvia sozinha),
  // então "acabou" com pendências na tela é o fim de verdade do lote.
  const isFinished =
    batchStarted &&
    !queue.isRunning &&
    queue.items.length > 0 &&
    !!createdExamId;
  const succeeded = isFinished && queue.counts.failed === 0;

  // Quem sai desta tela é a pessoa, no clique — nunca um timer. A fila é a
  // única memória da sessão sobre o que subiu, e o resultado ainda está sendo
  // lido quando o lote termina.
  const openExam = useCallback(() => {
    if (!createdExamId) return;
    // O total viaja pelo `state` do router: é dado desta transição, não do app,
    // e é o que deixa a faixa de sucesso aparecer do outro lado da navegação.
    void navigate(`/content/exams/${createdExamId}`, {
      state: { importedQuestions: queue.counts.done + resumedDone },
    });
  }, [createdExamId, navigate, queue.counts.done, resumedDone]);

  // Registro pequeno, reescrito a cada mudança da fila — é ele que sobrevive ao
  // F5. O `baseline` são as questões que já tinham subido e não estão nesta
  // fila: sem ele, retomar um lote apagaria do registro o que já foi feito.
  useEffect(() => {
    if (!batchStarted || !createdExamId || queue.items.length === 0) return;
    void saveImportState({
      examId: createdExamId,
      examLabel: examLabelRef.current,
      startedAt: startedAtRef.current,
      questions: [
        ...baselineRef.current,
        ...queue.items.map((item) => ({
          position: Number(item.id.replace('question-', '')),
          status: item.status,
          error: item.failure?.message,
        })),
      ],
    });
  }, [queue.items, batchStarted, createdExamId]);

  // Fila esvaziou: o cache de provas está velho de qualquer jeito.
  useEffect(() => {
    if (!isFinished) return;
    invalidateExams();
    // Fechou sem falha: não há nada a retomar, e o zip guardado só ocuparia
    // espaço no navegador de quem importou.
    if (queue.counts.failed === 0 && createdExamId) {
      void clearImport(createdExamId);
    }
  }, [isFinished, invalidateExams, queue.counts.failed, createdExamId]);

  return (
    <PageContainer maxWidth="4xl">
      <BackLink />

      <div>
        <h1 className="text-(--color-text-primary) text-2xl font-semibold">
          Importar prova de um CSV
        </h1>
        <p className="text-(--color-text-secondary) mt-1 text-sm">
          Envie um <code>.zip</code> com um único <code>.csv</code> na raiz e as
          figuras em <code>imagens/</code>. Nada é gravado antes da confirmação.
        </p>
      </div>

      {/* Com o lote em andamento (ou terminado), a pergunta não é mais "qual
          arquivo?" — e escolher outro aqui resetaria a fila, que é a única
          memória da sessão sobre o que subiu. */}
      {resumable && !batchStarted && !fileName && (
        <div className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 px-6 py-5 dark:border-amber-500/40 dark:bg-amber-500/5">
          <div>
            <p className="text-(--color-text-primary) text-lg font-semibold">
              Importação interrompida
            </p>
            <p className="text-(--color-text-secondary) text-sm">
              {resumable.examLabel} ·{' '}
              {resumable.questions.filter((q) => q.status === 'done').length} de{' '}
              {resumable.questions.length} questões subiram.
            </p>
          </div>

          <div>
            <p className="text-(--color-text-primary) text-sm font-medium">
              Não subiram ({pendingOnResumable.length}):
            </p>
            <p className="text-(--color-text-secondary) text-sm">
              {pendingOnResumable
                .slice(0, 20)
                .map((q) => q.position)
                .join(', ')}
              {pendingOnResumable.length > 20 &&
                ` e mais ${pendingOnResumable.length - 20}`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              disabled={isResuming}
              onClick={() => void handleResume(resumable)}
            >
              {isResuming
                ? 'Reabrindo o arquivo…'
                : `Reenviar as ${pendingOnResumable.length} que faltaram`}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={isResuming}
              onClick={() => void handleDiscardResumable(resumable.examId)}
            >
              Descartar
            </Button>
          </div>
          <p className="text-(--color-text-secondary) text-xs">
            O .zip desta importação ficou guardado neste navegador — reenviar
            não pede o arquivo de novo, e questão que já subiu não duplica.
          </p>
        </div>
      )}

      {!batchStarted && (
        <SectionCard title="Arquivo">
          <div className="space-y-3">
            <div
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-all duration-150 ${
                isBusy
                  ? 'border-(--color-border) opacity-60'
                  : isDragging
                    ? 'scale-[1.01] border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                    : 'border-(--color-border) hover:border-blue-400'
              }`}
            >
              {fileName && !isDragging ? (
                // Carregado: a caixa mostra o arquivo, não o convite. O ✕ tira ele a
                // qualquer momento e devolve a zona ao estado vazio.
                <div className="border-(--color-border) bg-(--color-bg-primary) flex w-full max-w-md items-center gap-3 rounded-lg border px-3 py-2 text-left">
                  <span aria-hidden className="text-2xl leading-none">
                    📦
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-(--color-text-primary) block truncate text-sm font-medium">
                      {fileName}
                    </span>
                    <span className="text-(--color-text-secondary) block text-xs">
                      {fileSize !== null && formatBytes(fileSize)}
                      {fileStatus && ` · ${fileStatus}`}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={removeFile}
                    aria-label={`Remover ${fileName}`}
                    title="Remover arquivo"
                    className="text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-lg leading-none transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <span
                    aria-hidden
                    className={`text-4xl leading-none ${
                      isDragging
                        ? 'animate-bounce motion-reduce:animate-none'
                        : ''
                    }`}
                  >
                    {isDragging ? '📥' : '🗂️'}
                  </span>
                  <Button
                    type="button"
                    size="lg"
                    disabled={isBusy}
                    onClick={() => zipInputRef.current?.click()}
                  >
                    Enviar arquivo
                  </Button>
                  <p
                    className={`text-sm ${
                      isDragging
                        ? 'font-medium text-blue-600 dark:text-blue-400'
                        : 'text-(--color-text-secondary)'
                    }`}
                  >
                    {isDragging ? (
                      'Solte o arquivo aqui'
                    ) : (
                      <>
                        ou arraste o <code>.zip</code> para esta área
                      </>
                    )}
                  </p>
                </>
              )}
              {/* Não existe input de arquivo no design-system — o nativo fica
                escondido, e o `Button` acima é quem abre o seletor. */}
              <input
                ref={zipInputRef}
                type="file"
                accept=".zip,application/zip"
                disabled={isBusy}
                className="hidden"
                onChange={(e) => {
                  // Permite reenviar o MESMO arquivo depois de corrigi-lo no disco.
                  const chosen = e.target.files ? [...e.target.files] : [];
                  e.target.value = '';
                  if (chosen.length) acceptZip(chosen);
                }}
              />
            </div>
            {readError && <p className="text-sm text-red-600">{readError}</p>}
            {validate.error && (
              <p className="text-sm text-red-600">
                {getApiErrorMessage(validate.error, 'Falha ao validar a prova')}
              </p>
            )}
          </div>
        </SectionCard>
      )}

      {clientErrors.length > 0 && (
        <SectionCard title={`Erros no arquivo (${clientErrors.length})`}>
          <ErrorList
            errors={clientErrors.map((error) => ({
              label: error.line === null ? 'arquivo' : `linha ${error.line}`,
              message: error.message,
            }))}
          />
        </SectionCard>
      )}

      {(imageIssues.length > 0 || imagesFixed.length > 0) && (
        <SectionCard
          title="Imagens"
          action={
            <span className="text-(--color-text-secondary) text-xs">
              {imageIssues.length > 0
                ? `${imageIssues.length} para resolver antes de importar`
                : `${imagesFixed.length} ajustada(s)`}
            </span>
          }
        >
          <div className="space-y-3">
            {imageIssues.map((issue) => (
              <ImportIssueCard
                key={issue.filename}
                title={
                  issue.positions.length > 0
                    ? `Questão ${issue.positions.join(', ')}`
                    : issue.filename
                }
                badge={
                  issue.reason === 'TOO_LARGE'
                    ? 'imagem grande demais'
                    : 'imagem em formato não aceito'
                }
                message={describeIssue(issue)}
                detail={
                  issue.positions.length > 1
                    ? `A mesma imagem é citada nas questões ${issue.positions.join(', ')}.`
                    : undefined
                }
                actions={
                  <>
                    {issue.reason === 'CONVERTIBLE' && (
                      <Button
                        type="button"
                        disabled={busyFilename === issue.filename}
                        onClick={() => void handleConvert(issue)}
                      >
                        {busyFilename === issue.filename
                          ? 'Convertendo…'
                          : 'Converter para WebP'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={busyFilename === issue.filename}
                      onClick={() => {
                        replacingRef.current = issue;
                        replaceInputRef.current?.click();
                      }}
                    >
                      Trocar arquivo
                    </Button>
                  </>
                }
              />
            ))}

            {imagesFixed.map((fixed) => (
              <p
                key={fixed.filename}
                className="text-(--color-text-secondary) text-xs"
              >
                ✓ <code>{fixed.filename}</code> — {fixed.note}. O nome no CSV
                não mudou.
              </p>
            ))}
          </div>
        </SectionCard>
      )}

      {parsed && validate.data && (
        <SectionCard title="Confirmação">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Field
                label="Instituição"
                value={
                  institution
                    ? `(${institution.initials}-${institution.uf}) ${institution.name}`
                    : null
                }
              />
              <Field label="Ano" value={parsed.examYear} />
              <Field label="Variante" value={parsed.examVariant} />
              {/* Trilha vem do ARQUIVO (coluna `exam_type`), não de um select:
                  é atributo da prova de origem, como ano e variante. Aparece
                  aqui para conferência antes de confirmar. */}
              <Field label="Trilha" value={EXAM_TYPE_LABELS[parsed.examType]} />
              <Field label="Questões" value={parsed.rows.length} />
            </div>

            <ClassificationSummary rows={parsed.rows} />
            <CommentSummary rows={parsed.rows} />

            {/* O erro de instituição deixa de ser beco sem saída: cada razão
                leva ao lugar onde ela se resolve, e "Validar de novo" repete o
                dry-run sem F5 e sem reabrir o zip (o lote vive no IndexedDB).

                `NAME_MISMATCH` está aqui, e não como aviso amarelo: é a chave
                inteira (sigla, UF, nome) que identifica a instituição na
                planilha. Com só uma parecida cadastrada, ou a planilha está
                errada ou é outra instituição — a mensagem do back traz o nome
                cadastrado, e os dois caminhos ficam abertos. */}
            {institutionError && (
              <div className="space-y-2">
                <p className="text-sm text-red-600">
                  {institutionError.reason === 'NOT_FOUND' ? (
                    <>
                      Nenhuma instituição ativa com a sigla “
                      {parsed.institutionInitials}” em {parsed.institutionUf}.
                      Cadastre-a primeiro — a importação não cria instituição.
                    </>
                  ) : (
                    institutionError.message
                  )}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  {institutionError.reason !== 'MALFORMED_LABEL' && (
                    // Pré-preenchido com o que estava na planilha, nome
                    // incluído (ele também tem de bater). Com a sigla já
                    // preenchida, o painel de duplicata aparece de saída: pode
                    // haver uma arquivada ou uma parecida — e aí o caminho é
                    // abrir aquela linha, não criar uma segunda.
                    <Link
                      to={`/content/institutions?new=1&initials=${encodeURIComponent(parsed.institutionInitials)}&uf=${encodeURIComponent(parsed.institutionUf)}&name=${encodeURIComponent(parsed.institutionName)}`}
                      className="underline"
                    >
                      Cadastrar instituição
                    </Link>
                  )}
                  {institutionError.reason !== 'NOT_FOUND' && (
                    <Link
                      to={`/content/institutions?search=${encodeURIComponent(parsed.institutionInitials)}&includeDeleted=true`}
                      className="underline"
                    >
                      Abrir a tela de instituições
                    </Link>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={validate.isPending}
                    onClick={revalidate}
                  >
                    {validate.isPending ? 'Validando…' : 'Validar de novo'}
                  </Button>
                </div>
              </div>
            )}

            {existingExam && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Já existe uma prova com essa instituição, ano e variante (
                {CONTENT_STATUS_LABELS[existingExam.status]}) —{' '}
                <Link
                  to={`/content/exams/${existingExam.id}`}
                  className="underline"
                >
                  abrir a existente
                </Link>
                . Continuar cria uma segunda prova.
              </p>
            )}

            {parsed.ignoredColumns.length > 0 && (
              <p className="text-(--color-text-secondary) text-xs">
                Colunas ignoradas (não fazem parte do formato):{' '}
                {parsed.ignoredColumns.join(', ')}.
              </p>
            )}

            {serverErrors.length > 0 && (
              <ErrorList
                errors={serverErrors.map((error) => ({
                  label: `questão ${error.position}`,
                  message: error.message,
                }))}
              />
            )}

            <p className="text-(--color-text-secondary) text-sm">
              A importação cria uma prova real. Para montar um simulado, crie-o
              e aponte para as questões de uma prova já importada.
            </p>

            {createItem.error && (
              <p className="text-sm text-red-600">
                {getApiErrorMessage(createItem.error, 'Falha ao criar a prova')}
              </p>
            )}

            <div className="flex items-center justify-end gap-3">
              {imageIssues.length > 0 && (
                <span className="text-(--color-text-secondary) text-xs">
                  resolva as {imageIssues.length} pendência(s) de imagem para
                  liberar a importação
                </span>
              )}
              <Button
                type="button"
                disabled={!canConfirm || createItem.isPending}
                onClick={() => void handleConfirm()}
              >
                {createItem.isPending
                  ? 'Criando a prova…'
                  : `Importar ${parsed.rows.length} questões`}
              </Button>
            </div>
          </div>
        </SectionCard>
      )}

      <RequestQueuePanel
        title="Fila de importação"
        doneLabel="importadas"
        items={queue.items}
        counts={queue.counts}
        onRetry={queue.retry}
      />

      {succeeded && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-green-300 bg-green-50 px-6 py-8 text-center dark:border-green-500/40 dark:bg-green-500/5">
          <span
            aria-hidden
            className="flex h-12 w-12 items-center justify-center rounded-full bg-green-600 text-2xl text-white"
          >
            ✓
          </span>
          <div>
            <p className="text-(--color-text-primary) text-lg font-semibold">
              Prova importada
            </p>
            <p className="text-(--color-text-secondary) text-sm">
              {queue.counts.done + resumedDone} de{' '}
              {queue.items.length + resumedDone}{' '}
              {queue.items.length + resumedDone === 1
                ? 'questão importada'
                : 'questões importadas'}
              {parsed && ` · ${parsed.examVariant} ${parsed.examYear}`}.
              {resumedDone > 0 &&
                ` ${queue.counts.done} ${queue.counts.done === 1 ? 'foi reenviada' : 'foram reenviadas'} agora.`}
            </p>
          </div>
          {/* "Importada" se lê como "no ar". É aqui que a diferença entre as
              duas coisas tem de ser dita — senão a prova fica pronta e
              invisível para o aluno sem ninguém notar. */}
          <p className="text-(--color-text-primary) max-w-md rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-500/40 dark:bg-amber-500/5">
            <strong>A prova ainda não está publicada.</strong> Ela foi criada
            como rascunho — abra a página da prova para conferir as questões e
            publicar no botão “Publicar tudo”.
          </p>
          <Button type="button" size="lg" onClick={openExam}>
            Abrir a prova
          </Button>
        </div>
      )}

      {isFinished && queue.counts.failed > 0 && (
        <div className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 px-6 py-6 dark:border-amber-500/40 dark:bg-amber-500/5">
          <p className="text-(--color-text-primary) text-lg font-semibold">
            Importação concluída com falhas
          </p>
          <p className="text-(--color-text-secondary) text-sm">
            {queue.counts.done + resumedDone} de{' '}
            {queue.items.length + resumedDone} questões subiram — e as que
            subiram já são reais. Resolva as pendências da fila acima (elas
            reenviam para a mesma prova) ou abra a prova como está. O que faltou
            continua guardado: se sair desta tela, o lote reaparece aqui para
            reenviar.
          </p>
          <div>
            <Button type="button" variant="secondary" onClick={openExam}>
              Abrir a prova
            </Button>
          </div>
        </div>
      )}

      {/* Um só input para todas as pendências: o `<Button>` do design-system dá
          a aparência, e quem pediu a troca fica no ref. */}
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Permite reescolher o MESMO arquivo depois de uma recusa.
          e.target.value = '';
          if (file) void handleReplaceChosen(file);
        }}
      />
    </PageContainer>
  );
}

/** kB/MB com uma casa — o suficiente para reconhecer o arquivo, não para medir. */
/**
 * O que a coluna `classification` diz sobre esta prova, na tela de confirmação.
 *
 * Existe porque a classificação é **opcional e silenciosa**: uma prova sem a
 * coluna importa igual, e um arquivo em que só metade das linhas foi preenchida
 * também. Sem este resumo, a diferença entre "80 questões classificadas" e "3
 * classificadas, 77 sem assunto" só apareceria questão por questão no preview,
 * depois de a prova estar importada.
 *
 * Não é bloqueio nem aviso vermelho — importar sem classificar é um caminho
 * legítimo (classifica-se depois pelo preview). É informação para quem confirma.
 */
function ClassificationSummary({ rows }: { rows: ImportRowDraft[] }) {
  const classified = rows.filter((row) => row.subjectNames.length > 0);
  const subjects = [...new Set(rows.flatMap((row) => row.subjectNames))].sort(
    (a, b) => a.localeCompare(b, 'pt'),
  );

  if (classified.length === 0) {
    return (
      <p className="text-(--color-text-secondary) text-sm">
        Nenhuma questão traz classificação — a coluna{' '}
        <code>classification</code> está ausente ou vazia. A prova importa assim
        mesmo; dá para classificar questão por questão no preview depois.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-(--color-text-primary) text-sm">
        {classified.length} de {rows.length}{' '}
        {rows.length === 1 ? 'questão traz' : 'questões trazem'} classificação
        {classified.length < rows.length && (
          <> — as outras {rows.length - classified.length} entram sem assunto</>
        )}
        .
      </p>
      <p className="text-(--color-text-secondary) text-xs">
        Assuntos citados: {subjects.join(', ')}. Assunto que não estiver
        cadastrado aparece na lista de erros acima — a importação não cria
        assunto novo.
      </p>
    </div>
  );
}

/**
 * Quantas questões sobem com comentário oficial. Existe pelo mesmo motivo do
 * resumo de classificação: a coluna é opcional e silenciosa, e "3 de 80
 * comentadas" só apareceria questão por questão depois da prova importada.
 */
function CommentSummary({ rows }: { rows: ImportRowDraft[] }) {
  const commented = rows.filter((row) => row.comment.trim() !== '');
  if (commented.length === 0) {
    return (
      <p className="text-(--color-text-secondary) text-sm">
        Nenhuma questão traz comentário oficial — a coluna <code>comment</code>{' '}
        está ausente ou vazia. Dá para escrever depois, editando cada questão no
        preview.
      </p>
    );
  }

  const withoutAuthor = commented.filter(
    (row) => row.commentAuthor.trim() === '',
  ).length;

  return (
    <p className="text-(--color-text-primary) text-sm">
      {commented.length} de {rows.length}{' '}
      {rows.length === 1 ? 'questão traz' : 'questões trazem'} comentário
      oficial
      {withoutAuthor > 0 && (
        <>
          {' '}
          —{' '}
          <span className="text-(--color-text-secondary)">
            {withoutAuthor}{' '}
            {withoutAuthor === 1 ? 'sem autor' : 'sem autor preenchido'}
          </span>
        </>
      )}
      .
    </p>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} kB` : `${(kb / 1024).toFixed(1)} MB`;
}

function ErrorList({
  errors,
}: {
  errors: { label: string; message: string }[];
}) {
  return (
    <ul className="space-y-1">
      {errors.map((error, index) => (
        <li key={index} className="text-sm text-red-600">
          <span className="font-medium">{error.label}:</span> {error.message}
        </li>
      ))}
    </ul>
  );
}

function BackLink() {
  return (
    <Link
      to="/content/exams"
      className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm"
    >
      ← Voltar para Provas
    </Link>
  );
}
